import { useEffect, useState } from "react";
import { CatalogRow, listMethodologyCatalog } from "@/api/methodologyConsole";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DateLabel,
  EmptyState,
  ErrorState,
  HashLabel,
  ImmutabilityNote,
  LifecycleBadge,
  LoadingState,
} from "./primitives";

const TYPES: Array<{ value: string; label: string }> = [
  { value: "control_mapping_version", label: "Control mapping versions" },
  { value: "scoring_model_version", label: "Scoring model versions" },
  { value: "questionnaire_version", label: "Questionnaire versions" },
  { value: "reporting_template_version", label: "Reporting template versions" },
  { value: "report_component_version", label: "Report component versions" },
];

export default function CatalogTab({
  defaultType = "control_mapping_version",
}: {
  defaultType?: string;
}) {
  const [type, setType] = useState(defaultType);
  const [rows, setRows] = useState<CatalogRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setRows(null);
    setError(null);
    listMethodologyCatalog(type)
      .then(setRows)
      .catch((e: Error) => setError(e.message));
  }, [type]);

  return (
    <div className="space-y-4">
      <Select value={type} onValueChange={setType}>
        <SelectTrigger className="w-80" aria-label="Artifact type">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {TYPES.map((t) => (
            <SelectItem key={t.value} value={t.value}>
              {t.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {error ? (
        <ErrorState message={error} />
      ) : !rows ? (
        <LoadingState label="Loading catalog…" />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No artifacts of this type"
          description="Nothing of this artifact type has been versioned in this environment."
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Artifact</TableHead>
              <TableHead>Version</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Content hash</TableHead>
              <TableHead>Effective from</TableHead>
              <TableHead>Published</TableHead>
              <TableHead>Detail</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">{r.artifact_key}</TableCell>
                <TableCell>{r.version_label}</TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <LifecycleBadge status={r.status} />
                    <div>
                      <ImmutabilityNote status={r.status} />
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <HashLabel value={r.content_hash} />
                </TableCell>
                <TableCell className="text-sm">
                  <DateLabel value={r.effective_from} />
                </TableCell>
                <TableCell className="text-sm">
                  <DateLabel value={r.published_at} />
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {Object.entries(r.detail ?? {})
                    .filter(([, v]) => v !== null && v !== "")
                    .map(([k, v]) => `${k.replace(/_/g, " ")}: ${String(v)}`)
                    .join(" · ") || "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
